// Escrituras de la selección: una fila por sesión donde cada tarea lee la base, escribe y la guarda.
import type { AnchorGiftLine } from '@/domains/sales/wizard/promotionSlots';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import type { ReservedLine } from '@/domains/sales/wizard/types';
import type {
    EvaluateSalePromotionsRequest,
    EvaluateSalePromotionsResponse,
    HoldSalePromotionRequest,
    HoldSalePromotionResponse,
    LinePromotionDiscount,
    ReleaseSalePromotionResponse,
} from '../api/wizardPromotionsContract';
import {
    blockedDrops,
    droppedCodeOf,
    EMPTY_SELECTION,
    isEmptySelection,
    reconcileSelection,
    selectionFromHeld,
    stalePromotionIds,
    toHoldRequest,
    withStackable,
    type BlockedDrop,
    type DroppedCode,
    type PromotionSelection,
} from './promotionSelection';

export interface PromotionsApi {
    evaluate: (request: EvaluateSalePromotionsRequest) => Promise<EvaluateSalePromotionsResponse>;
    hold: (
        sessionId: string,
        request: HoldSalePromotionRequest,
    ) => Promise<HoldSalePromotionResponse>;
    release: (sessionId: string) => Promise<ReleaseSalePromotionResponse>;
}

export interface AppliedSelection {
    selection: PromotionSelection;
    lines: LinePromotionDiscount[];
    droppedCode: DroppedCode | null;
    /** Promos que se soltaron porque Promociones ya no las lista (pausadas). */
    droppedPromotionIds: string[];
    /** Promos que se soltaron aunque siguen listadas, con su motivo. */
    blockedDrops?: BlockedDrop[];
}

/** Dónde vive la selección: `read` da `null` mientras no se conozca lo que tiene la sesión. */
export interface SelectionPort {
    read: () => PromotionSelection | null;
    commit: (applied: AppliedSelection) => void;
}

/** El PUT reemplaza todo lo apartado: sin conocer la sesión, escribir borraría lo que ya tiene. */
export class PromotionBaseUnknownError extends Error {
    constructor(sessionId: string) {
        super(`Aún no se conoce lo apartado en la sesión ${sessionId}`);
        this.name = 'PromotionBaseUnknownError';
    }
}

const queues = new Map<string, Promise<unknown>>();

/** Una escritura a la vez por sesión: dos PUT cruzados darían SALE_PROMOTION_HOLD_CONFLICT. */
function enqueue<T>(sessionId: string, task: () => Promise<T>): Promise<T> {
    const previous = queues.get(sessionId) ?? Promise.resolve();
    const next = previous.then(task, task);
    queues.set(
        sessionId,
        next.catch(() => undefined),
    );
    return next;
}

async function write(
    sessionId: string,
    next: PromotionSelection,
    api: PromotionsApi,
): Promise<AppliedSelection> {
    if (isEmptySelection(next)) {
        await api.release(sessionId);
        return { selection: next, lines: [], droppedCode: null, droppedPromotionIds: [] };
    }
    const held = await api.hold(sessionId, toHoldRequest(next));
    const selection = selectionFromHeld(held, next.anchorLineId, next);
    return { selection, lines: held.lines, droppedCode: null, droppedPromotionIds: [] };
}

async function reapply(
    sessionId: string,
    base: PromotionSelection,
    api: PromotionsApi,
): Promise<AppliedSelection> {
    const evaluation = await api.evaluate({ sessionId, discountCode: base.code });
    const applied = await write(sessionId, reconcileSelection(base, evaluation), api);
    return {
        ...applied,
        droppedCode: droppedCodeOf(base, evaluation),
        droppedPromotionIds: stalePromotionIds(base, evaluation),
        blockedDrops: blockedDrops(base, evaluation),
    };
}

/** Cambio de la selección: recibe lo que tenga la sesión en su turno y devuelve lo nuevo. */
export type SelectionChange = (base: PromotionSelection) => PromotionSelection;

/** Aplica un cambio sobre lo que haya en la sesión al momento de su turno en la fila. */
export function applyChange(
    sessionId: string,
    change: SelectionChange,
    port: SelectionPort,
    api: PromotionsApi,
): Promise<AppliedSelection> {
    return enqueue(sessionId, async () => {
        const base = port.read();
        if (!base) throw new PromotionBaseUnknownError(sessionId);
        const applied = await write(sessionId, change(base), api);
        port.commit(applied);
        return applied;
    });
}

/**
 * Reevalúa y aparta lo que sigue aplicando; `null` si no hay nada o aún no se conoce la sesión.
 * `extend` ajusta la base en su turno (p. ej. regresar lo soltado al dejar SureKeep).
 */
export function reapplySelection(
    sessionId: string,
    port: SelectionPort,
    api: PromotionsApi,
    extend: SelectionChange = (base) => base,
): Promise<AppliedSelection | null> {
    return enqueue(sessionId, async () => {
        const read = port.read();
        const base = read && extend(read);
        if (!base || isEmptySelection(base)) return null;
        const applied = await reapply(sessionId, base, api);
        port.commit(applied);
        return applied;
    });
}

/** Tras derivar: reaplica lo conocido; sin memoria suelta todo, una promo solo SureKeep no pasa. */
export function reapplyOrRelease(
    sessionId: string,
    port: SelectionPort,
    api: PromotionsApi,
): Promise<AppliedSelection> {
    return enqueue(sessionId, async () => {
        const base = port.read();
        const applied =
            base && !isEmptySelection(base)
                ? await reapply(sessionId, base, api)
                : await write(sessionId, EMPTY_SELECTION, api);
        port.commit(applied);
        return applied;
    });
}

/** Lo que el wizard sabe de la sesión junto a la ancla. */
export type SessionPromotionContext = Pick<
    AnchorSlotContext,
    'anchorLine' | 'salePromotion' | 'salePromotions' | 'anchorGiftLines' | 'extraLines'
>;

/** Código heredado por la cascada de ancla con descuento 0, o la marca del wizard en la línea. */
export function needsRevalidation(line: ReservedLine | AnchorGiftLine): boolean {
    if (line.promotionRevalidationPending === true) return true;
    const { discountCode, discountCents } = line as Partial<ReservedLine>;
    return Boolean(discountCode) && !(discountCents && discountCents > 0);
}

/** Alguna línea de la ancla o la promo de venta esperan que Promociones las revalide (REQ-13). */
export function contextNeedsRevalidation(context: SessionPromotionContext): boolean {
    const { anchorLine, salePromotion, anchorGiftLines } = context;
    return (
        needsRevalidation(anchorLine) ||
        anchorGiftLines.some((l) => l.promotionRevalidationPending === true) ||
        (salePromotion !== undefined && salePromotion.promotionVersion === undefined)
    );
}

/** Descuento por promo de la línea; en líneas viejas se deriva de `promotionId`. */
export function lineDiscounts(
    line: Pick<
        ReservedLine,
        | 'promotionId'
        | 'promotionVersion'
        | 'discountCents'
        | 'promotionDiscounts'
        | 'codeDiscountCents'
    >,
): { promotionId: string; promotionVersion?: number; discountCents: number }[] {
    if (line.promotionDiscounts) return line.promotionDiscounts;
    if (!line.promotionId) return [];
    const discountCents = (line.discountCents ?? 0) - (line.codeDiscountCents ?? 0);
    return [
        {
            promotionId: line.promotionId,
            ...(line.promotionVersion !== undefined && { promotionVersion: line.promotionVersion }),
            discountCents,
        },
    ];
}

/** Lo que ya tiene la sesión (promos por línea, de venta, regalos y código), todo como acumulable;
 * con la evaluación, `normalizeKinds` acomoda la que va sola. */
export function seedFromSession(context: SessionPromotionContext): PromotionSelection {
    const { anchorLine, anchorGiftLines } = context;
    const extraLines = context.extraLines ?? [];
    const salePromotions =
        context.salePromotions ?? (context.salePromotion ? [context.salePromotion] : []);
    let selection: PromotionSelection = {
        anchorLineId: anchorLine.lineId,
        byLine: {},
        code: anchorLine.discountCode ?? null,
    };
    const placed = new Set<string>();
    const giftSkusOf = (promotionId: string) =>
        anchorGiftLines.filter((l) => l.promotionId === promotionId).map((l) => l.sku);
    const add = (lineId: string, promotionId: string, promotionVersion: number | undefined) => {
        placed.add(promotionId);
        selection = withStackable(
            selection,
            lineId,
            {
                promotionId,
                ...(promotionVersion !== undefined && { promotionVersion }),
                giftSkus: lineId === anchorLine.lineId ? giftSkusOf(promotionId) : [],
            },
            true,
        );
    };
    for (const line of [anchorLine, ...extraLines]) {
        for (const d of lineDiscounts(line)) {
            if (d.discountCents > 0) add(line.lineId, d.promotionId, d.promotionVersion);
        }
    }
    for (const ref of salePromotions) {
        if (!placed.has(ref.promotionId))
            add(anchorLine.lineId, ref.promotionId, ref.promotionVersion);
    }
    for (const gift of anchorGiftLines) {
        if (gift.promotionId && !placed.has(gift.promotionId)) {
            add(anchorLine.lineId, gift.promotionId, gift.promotionVersion);
        }
    }
    return selection;
}
